import { supabase } from '@/integrations/supabase/client'

// Helper function to strip EXIF data from images
function stripEXIFFromImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file) // Return original file for non-images
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      // Set canvas dimensions to image dimensions
      canvas.width = img.width
      canvas.height = img.height
      
      // Draw image to canvas (this strips EXIF data)
      ctx?.drawImage(img, 0, 0)
      
      // Convert back to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const cleanFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          })
          resolve(cleanFile)
        } else {
          reject(new Error('Failed to process image'))
        }
      }, file.type, 0.95) // 95% quality - preserve more detail for AI analysis
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}

export interface UploadedFile {
  path: string
  url: string
  name: string
  type: string
  size: number
}

export async function uploadFileToStorage(
  file: File,
  userId: string,
  conversationId: string,
  category?: string
): Promise<UploadedFile> {
  try {
    // Strip EXIF data from images for privacy
    const cleanFile = await stripEXIFFromImage(file)
    
    // Generate unique filename
    const timestamp = Date.now()
    const fileExtension = cleanFile.name.split('.').pop()
    const fileName = `${timestamp}-${Math.random().toString(36).substring(2)}.${fileExtension}`
    
    // Create folder structure: {userId}/{conversationId}/{category}/{fileName}
    const folderPath = category 
      ? `${userId}/${conversationId}/${category}/${fileName}`
      : `${userId}/${conversationId}/${fileName}`
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('conversation-attachments')
      .upload(folderPath, cleanFile, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) {
      throw new Error(`Upload failed: ${error.message}`)
    }

    // Get signed URL (expires in 1 hour for security)
    const { data: signedUrlData } = await supabase.storage
      .from('conversation-attachments')
      .createSignedUrl(folderPath, 3600) // 1 hour expiry

    return {
      path: data.path,
      url: signedUrlData?.signedUrl || '',
      name: cleanFile.name,
      type: cleanFile.type,
      size: cleanFile.size
    }
  } catch (error) {
    console.error('File upload error:', error)
    throw error
  }
}

export async function saveAttachmentToDatabase(
  messageId: string,
  uploadedFile: UploadedFile,
  userId: string,
  conversationId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('message_attachments')
      .insert({
        message_id: messageId,
        file_path: uploadedFile.path,
        file_name: uploadedFile.name,
        file_type: uploadedFile.type,
        file_size: uploadedFile.size,
        user_id: userId,
        conversation_id: conversationId
      } as any)

    if (error) {
      throw new Error(`Database save failed: ${error.message}`)
    }
  } catch (error) {
    console.error('Database save error:', error)
    throw error
  }
}

export async function addToUserFileLibrary(
  uploadedFile: UploadedFile,
  userId: string,
  category: string,
  description?: string,
  tags?: string[]
): Promise<void> {
  try {
    const { error } = await supabase
      .from('user_file_library')
      .insert({
        user_id: userId,
        file_path: uploadedFile.path,
        file_name: uploadedFile.name,
        file_type: uploadedFile.type,
        category,
        description,
        tags: tags || []
      } as any)

    if (error) {
      throw new Error(`File library save failed: ${error.message}`)
    }
  } catch (error) {
    console.error('File library save error:', error)
    throw error
  }
}

export async function getUserFileLibrary(
  userId: string,
  category?: string
): Promise<unknown[]> {
  try {
    let query = supabase
      .from('user_file_library')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (category) {
      query = query.eq('category', category)
    }

    const { data, error } = await query

    if (error) {
      throw new Error(`File library fetch failed: ${error.message}`)
    }

    return data || []
  } catch (error) {
    console.error('File library fetch error:', error)
    throw error
  }
}

export function getFileCategoryFromType(fileType: string): string {
  if (fileType.startsWith('image/')) {
    return 'images'
  } else if (fileType === 'application/pdf') {
    return 'documents'
  } else if (fileType.includes('word') || fileType.includes('excel')) {
    return 'documents'
  } else if (fileType === 'text/plain') {
    return 'documents'
  }
  return 'other'
}

// Function to refresh signed URLs when they expire
export async function refreshSignedUrl(filePath: string): Promise<string> {
  try {
    const { data, error } = await supabase.storage
      .from('conversation-attachments')
      .createSignedUrl(filePath, 3600) // 1 hour expiry

    if (error) {
      throw new Error(`Failed to refresh signed URL: ${error.message}`)
    }

    return data?.signedUrl || ''
  } catch (error) {
    console.error('Error refreshing signed URL:', error)
    throw error
  }
}

export function getSmartCategoryFromContext(
  fileType: string,
  conversationCategory?: string,
  userMessage?: string
): string {
  const message = userMessage?.toLowerCase() || ''
  
  // Image categorization based on context
  if (fileType.startsWith('image/')) {
    if (message.includes('skin') || message.includes('face') || message.includes('beauty')) {
      return 'skin-photos'
    } else if (message.includes('outfit') || message.includes('clothes') || message.includes('style')) {
      return 'style-preferences'
    } else if (message.includes('home') || message.includes('room') || message.includes('house')) {
      return 'home-photos'
    } else if (message.includes('car') || message.includes('vehicle') || message.includes('engine')) {
      return 'automotive-photos'
    } else if (message.includes('plant') || message.includes('garden') || message.includes('tree')) {
      return 'plant-photos'
    } else if (message.includes('pet') || message.includes('dog') || message.includes('cat')) {
      return 'pet-photos'
    } else if (message.includes('ingredient') || message.includes('food') || message.includes('fridge')) {
      return 'cooking-photos'
    }
  }
  
  // Document categorization
  if (fileType === 'application/pdf' || fileType.includes('document')) {
    if (message.includes('receipt') || message.includes('expense') || message.includes('bill')) {
      return 'financial-documents'
    } else if (message.includes('recipe') || message.includes('cooking')) {
      return 'cooking-documents'
    }
  }
  
  // Default based on conversation category
  switch (conversationCategory) {
    case 'Beauty & Skincare':
      return 'beauty-photos'
    case 'Fashion & Style':
      return 'style-photos'
    case 'Home & DIY':
      return 'home-photos'
    case 'Automotive':
      return 'automotive-photos'
    case 'Cooking':
      return 'cooking-photos'
    case 'Finance & Retirement':
      return 'financial-documents'
    default:
      return getFileCategoryFromType(fileType)
  }
}
